import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { authService } from '../../modules/auth/services/authService';

/**
 * RoleBasedProtectedRoute - Protects routes based on user roles
 * 
 * @param {Array} allowedRoles - Array of roles that can access this route (e.g., ['doctor', 'admin'])
 * @param {string} redirectTo - Where to redirect unauthorized users (default: /auth/role-selection)
 */
const RoleBasedProtectedRoute = ({ allowedRoles = [], redirectTo = '/auth/unauthorized' }) => {
  const isAuthenticated = authService.isAuthenticated();
  const selectedRole = localStorage.getItem('selectedRole');
  const currentUser = authService.getCurrentUser();

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  // Check if user object exists
  if (!currentUser) {
    console.error('🔒 No user data found, redirecting to login');
    authService.logout();
    return <Navigate to="/auth/login" replace />;
  }

  // Check if role is selected
  if (!selectedRole) {
    return <Navigate to="/auth/role-selection" replace />;
  }

  // Validate that selectedRole is in allowedRoles
  if (!allowedRoles.includes(selectedRole)) {
    console.error(`🔒 Role "${selectedRole}" not allowed for this route. Allowed: ${allowedRoles.join(', ')}`);
    return <Navigate to={redirectTo} replace />;
  }

  // Validate that user actually HAS the selected role
  const userHasRole = validateUserRole(currentUser, selectedRole);
  if (!userHasRole) {
    console.error(`🔒 User does not have role "${selectedRole}". User roles:`, {
      is_doctor: currentUser.is_doctor,
      is_clinician: currentUser.is_clinician, 
      is_patient: currentUser.is_patient,
      is_admin: currentUser.is_superuser
    });
    
    // Clear invalid role selection and redirect
    localStorage.removeItem('selectedRole');
    return <Navigate to="/auth/role-selection" replace state={{ 
      error: `You do not have permission to access the ${selectedRole} module.` 
    }} />;
  }

  // All checks passed, render the protected content
  return <Outlet />;
};

/**
 * Validate that the current user actually has the role they selected
 * 
 * @param {Object} user - Current user object
 * @param {string} role - Selected role to validate
 * @returns {boolean} - True if user has the role
 */
const validateUserRole = (user, role) => {
  if (!user || !role) return false;

  switch (role) {
    case 'doctor':
      return user.is_doctor === true;
      
    case 'clinician':
      return user.is_clinician === true;
      
    case 'patient':
      // Check the actual is_patient field from the database
      return user.is_patient === true;
      
    case 'admin':
      return user.is_superuser === true;
      
    default:
      console.error(`🔒 Unknown role: ${role}`);
      return false;
  }
};

/**
 * Helper function to get all roles a user has
 * Useful for debugging and role selection page
 */
export const getUserRoles = (user) => {
  if (!user) return [];
  
  const roles = [];
  
  if (user.is_superuser) roles.push('admin');
  if (user.is_doctor) roles.push('doctor');
  if (user.is_clinician) roles.push('clinician');
  if (user.is_patient) roles.push('patient');
  
  return roles;
};

export default RoleBasedProtectedRoute;
